(() => {
  if (customElements.get('lzl-card-colours')) return;
  const cache = new Map();
  const familyCache = new Map();
  const isColour = name => /^(couleurs?|coloris|colou?rs?)$/i.test(String(name).trim());
  const normal = value => String(value || '').normalize('NFKC').toLowerCase().replace(/[“”]/g, '"').replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim();
  const labels = {black:'Noir',noir:'Noir',white:'Blanc',blanc:'Blanc',blanche:'Blanc',carbon:'Carbone',carbone:'Carbone','bleu azur':'Azur',azur:'Azur',cheery:'Cherry',cherry:'Cherry',petol:'Petrol',navy:'Navy',wine:'Wine','dark pink':'Rose foncé',kaki:'Kaki',petrol:'Petrol'};
  const colours = {navy:'#17233f',kaki:'#65684b','dark pink':'#984769',noir:'#000000',black:'#000000',azur:'#527887','bleu azur':'#527887',carbone:'#38282b',carbon:'#38282b',mulberry:'#71324f',cherry:'#862334',cheery:'#862334',petrol:'#21304a',petol:'#21304a',purple:'#66536e','purple smoke':'#9a8da2',wine:'#582d3b',cacao:'#634c40',rouge:'#c02630','electric red':'#c02630','forest green':'#254c40',jaune:'#dfce67','sunny yellow':'#dfce67','light blue':'#85a7ba','bleu clair':'#85a7ba','dark grey':'#55575b','barbie pink':'#d2729b',marron:'#76553e',brown:'#76553e',blanc:'#ffffff',white:'#ffffff'};

  Object.assign(colours, {beige:'#d1c1a2',grey:'#808080',gray:'#808080',gris:'#808080',blue:'#173c70',bleu:'#173c70',green:'#2c674a',vert:'#2c674a',pink:'#d887a0',rose:'#d887a0',purple:'#66536e',violet:'#66536e',red:'#c02630',orange:'#da772e',yellow:'#dfce67',cream:'#eee6d5',creme:'#eee6d5','crème':'#eee6d5',mocha:'#826757',anthracite:'#383b3f','bleu marine':'#17233f',marine:'#17233f'});
  function gender(title, tags) {
    const text = normal([title, ...(Array.isArray(tags) ? tags : String(tags || '').split(','))].join(' '));
    if (/(?:^|[\s_-])(unisex|unisexe|mixte)(?:$|[\s_-])/.test(text)) return 'unisex';
    const women = /(?:^|[\s_-])(femmes?|women|womens|female)(?:$|[\s_-])/.test(text);
    const men = /(?:^|[\s_-])(hommes?|men|mens|male)(?:$|[\s_-])/.test(text);
    return women && men ? 'unisex' : women ? 'women' : men ? 'men' : '';
  }
  function normalizeProduct(product) {
    if (!product) return null;
    product.options = product.options.map((o, i) => typeof o === 'string' ? {name:o, position:i+1, values:[...new Set(product.variants.map(v => (v.options || [v.option1,v.option2,v.option3])[i]))]} : o);
    product.variants.forEach(v => { v.options = v.options || [v.option1,v.option2,v.option3]; });
    return product;
  }
  function getProduct(root, handle) {
    const url = `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}.js`;
    if (!cache.has(url)) cache.set(url, fetch(url).then(r => r.ok ? r.json() : null).then(normalizeProduct).catch(() => null));
    return cache.get(url);
  }
  function modelName(title, options) {
    const name = normal(title);
    const option = options.find(o => isColour(o.name));
    if (!option) return name;
    const values = option.values.flatMap(value => [normal(value), normal(labels[normal(value)] || value)]);
    const match = name.match(/^(.*?)\s+[-–—:]\s+([^]+)$/);
    return match && values.includes(match[2]) ? match[1].trim() : name;
  }
  async function findFamily(el, current) {
    const audience = gender(el.dataset.title, JSON.parse(el.dataset.tags || '[]'));
    if (!audience) return [];
    const model = modelName(el.dataset.title, current?.options || []);
    const key = [el.dataset.search, model, normal(el.dataset.type), audience].join('|');
    if (!familyCache.has(key)) familyCache.set(key, (async () => {
      const found = []; let page = 1;
      while (page) {
        const url = new URL(el.dataset.search, window.location.origin);
        url.searchParams.set('q', `title:"${model.replace(/["\\]/g, ' ')}"`);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'none');
        url.searchParams.set('view', 'lzl-colours');
        url.searchParams.set('page', String(page));
        const response = await fetch(url);
        if (!response.ok) break;
        const data = await response.json();
        for (const entry of data.products || []) {
          const p = normalizeProduct(entry.product);
          if (p && modelName(p.title, p.options) === model && normal(p.type || p.product_type) === normal(el.dataset.type) && gender(p.title,p.tags) === audience) found.push(entry);
        }
        if (!data.next || data.next <= page) break;
        page = data.next;
      }
      return found;
    })().catch(() => []));
    return familyCache.get(key);
  }
  function swatchColour(value, native) {
    const key = normal(value);
    if (colours[key]) return colours[key];
    if (native && CSS.supports('color', native)) return native;
    return CSS.supports('color', key) ? key : '#777777';
  }
  class CardColours extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      if (this.dataset.mode === 'product' || !('IntersectionObserver' in window)) this.load();
      else {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
        }, {rootMargin:'250px'});
        this.observer.observe(this.closest('.card-wrapper') || this.parentElement);
      }
    }
    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
    async load() {
      const handles = [...new Set([this.dataset.current,...this.dataset.handles.split(',').map(h => h.trim())].filter(Boolean))];
      this.nativeSwatches = JSON.parse(this.querySelector('.lzl-native-swatches')?.textContent || '{}');
      const products = await Promise.all(handles.map(h => getProduct(this.dataset.root, h)));
      const entries = products.filter(Boolean).map(product => ({product,swatches:product.handle === this.dataset.current ? this.nativeSwatches : {}}));
      if (!this.isConnected) return;
      this.render(entries);
      const discovered = await findFamily(this, products.find(p => p?.handle === this.dataset.current));
      if (!this.isConnected) return;
      const byHandle = new Map(entries.map(entry => [entry.product.handle,entry]));
      discovered.forEach(entry => byHandle.set(entry.product.handle, entry));
      this.render([...byHandle.values()]);
    }
    render(entries) {
      const seen = new Set(); const links = [];
      const current = entries.find(e => e.product.handle === this.dataset.current)?.product;
      const selections = JSON.parse(this.dataset.selected || '[]');
      const sourceOptions = current?.options || [];
      for (const entry of entries) {
        const product = entry.product;
        const colour = product.options.find(o => isColour(o.name));
        if (!colour) continue;
        for (const value of colour.values) {
          const label = labels[normal(value)] || value;
          const key = normal(label);
          if (seen.has(key)) continue;
          const variants = product.variants.filter(v => v.options[colour.position - 1] === value);
          const sameOptions = v => product.options.every(o => isColour(o.name) || v.options[o.position-1] === selections[sourceOptions.findIndex(source => normal(source.name) === normal(o.name))]);
          const preserveSize = this.dataset.mode === 'product';
          const selected = product.handle === this.dataset.current && normal(value) === normal(this.dataset.colour);
          const variant = (preserveSize && selected ? variants.find(sameOptions) : null) || (preserveSize ? variants.find(v => v.available && sameOptions(v)) : null) || variants.find(v => v.available) || variants[0];
          if (!variant) continue;
          seen.add(key);
          const a = document.createElement('a');
          a.href = `${this.dataset.root.replace(/\/$/, '')}/products/${encodeURIComponent(product.handle)}?variant=${variant.id}`;
          const available = variants.some(v => v.available);
          const name = `${label}${available ? '' : ' — ' + this.dataset.soldOut}`;
          a.title = name; a.setAttribute('aria-label', name);
          if (product.handle === this.dataset.current && (!this.dataset.colour || selected)) a.setAttribute('aria-current', 'page');
          if (!available) a.dataset.soldOut = 'true';
          const swatch = document.createElement('span');
          swatch.className = 'lzl-auto-colour-dot';
          swatch.setAttribute('aria-hidden', 'true');
          swatch.style.backgroundColor = swatchColour(value, entry.swatches?.[value] || (product.handle === this.dataset.current ? this.nativeSwatches[value] : null));
          a.append(swatch);
          if (this.dataset.mode === 'product') {
            const text = document.createElement('span'); text.className = 'lzl-auto-colour-name'; text.textContent = name; a.append(text);
          }
          links.push(a);
        }
      }
      if (links.length > 1) {
        if (this.dataset.mode === 'product') {
          const heading = document.createElement('p'); heading.className = 'lzl-auto-colour-heading'; heading.textContent = this.dataset.heading;
          const list = document.createElement('div'); list.className = 'lzl-auto-colour-list'; list.append(...links);
          this.replaceChildren(heading,list);
        } else this.replaceChildren(...links);
        this.hidden = false;
      }
    }
  }
  customElements.define('lzl-card-colours', CardColours);
})();
